import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonList,
    IonAlert,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    word: string;
    options: string[];
    correctAnswer: string;
}


const RhymingWords: React.FC = () => {

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'RhymingWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/RhymingWords.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Rhyming Words – LKG English Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore fun and engaging rhyming word activities for LKG kids on Mama’s Guidance. Help children develop phonemic awareness through interactive games and colorful visuals.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Rhyming Words</h1>

                                <p>
                                    Let your child discover the joy of sounds with this delightful <strong>Rhyming Words</strong> worksheet! Perfect for LKG students, this activity introduces children to <strong>words that sound alike</strong>, helping them build early phonemic awareness and prepare for reading success.
                                </p>

                                <h2>🎵 What Are Rhyming Words?</h2>
                                <p>
                                    Rhyming words are words that have the same ending sound. For example: <b><em>cat – hat</em>, <em>sun – fun</em>, <em>dog – log</em></b>. Recognizing rhymes helps kids notice patterns in spoken language.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet includes:
                                </p>
                                <ul>
                                    <li>Matching rhyming pairs with pictures</li>
                                    <li>Circle the rhyming word from a list</li>
                                    <li>Simple read-aloud word pairs</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify rhyming pairs and sound patterns</li>
                                    <li>Improve listening and auditory discrimination</li>
                                    <li>Build early literacy and word recognition skills</li>
                                    <li>Enhance vocabulary and speech development</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Say the first word aloud and ask your child to listen.</li>
                                    <li>Present the other options and ask which one rhymes.</li>
                                    <li>Let them circle or match the correct rhyming word.</li>
                                    <li>Use songs, rhymes, or poems to reinforce the concept.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>This–That & These–Those</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/picture-composition"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Picture Composition</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Rhyming Is Important</h2>
                                <p>
                                    Rhyming helps children <strong>hear patterns and sounds</strong>, which is a major step in developing reading fluency. It also makes language fun, memorable, and musical!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Rhyming Words</h1>

                            <p>
                                Let your child discover the joy of sounds with this delightful <strong>Rhyming Words</strong> worksheet! Perfect for LKG students, this activity introduces children to <strong>words that sound alike</strong>, helping them build early phonemic awareness and prepare for reading success.
                            </p>

                            <h2>🎵 What Are Rhyming Words?</h2>
                            <p>
                                Rhyming words are words that have the same ending sound. For example: <b><em>cat – hat</em>, <em>sun – fun</em>, <em>dog – log</em></b>. Recognizing rhymes helps kids notice patterns in spoken language.
                            </p>

                            <h2>🧩 What’s in the Worksheet?</h2>
                            <p>
                                The worksheet includes:
                            </p>
                            <ul>
                                <li>Matching rhyming pairs with pictures</li>
                                <li>Circle the rhyming word from a list</li>
                                <li>Simple read-aloud word pairs</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify rhyming pairs and sound patterns</li>
                                <li>Improve listening and auditory discrimination</li>
                                <li>Build early literacy and word recognition skills</li>
                                <li>Enhance vocabulary and speech development</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Say the first word aloud and ask your child to listen.</li>
                                <li>Present the other options and ask which one rhymes.</li>
                                <li>Let them circle or match the correct rhyming word.</li>
                                <li>Use songs, rhymes, or poems to reinforce the concept.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>This–That & These–Those</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/picture-composition"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Picture Composition</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Rhyming Is Important</h2>
                            <p>
                                Rhyming helps children <strong>hear patterns and sounds</strong>, which is a major step in developing reading fluency. It also makes language fun, memorable, and musical!
                            </p>
                        </article>
                        <p>
                            <strong>Find the word that rhymes with:</strong>{" "}
                            <h3>{questions[currentIndex]?.word}</h3>
                        </p>

                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value!)}
                        >
                            <IonList className="grid-item-container">
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctAnswer
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonList>
                        </IonRadioGroup>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default RhymingWords;
